import { queryOptions, type QueryClient } from '@tanstack/react-query';
import type { SessionClient } from '../http';
import { fetchAccess, fetchIdentity, fetchOrgNodeTree } from './session-api';

/**
 * How queries relate to the selected org node.
 *
 * The session client adds `?orgNodeId=` to every scoped request on its own, so a query whose
 * key does not carry the scope would keep the previous node's answer after a switch. Every query
 * therefore declares one of:
 *
 * - `meta: { scope: 'independent' }` — the answer does not depend on the node (identity, tree);
 * - `meta: { scope: 'keyed' }` — the node is in the key, so a switch is a different query
 *   (configuration, access);
 * - nothing — the default for feature data: reset on every scope switch, which drops the old
 *   node's rows and refetches what is on screen (`resetScopeDependentQueries`).
 */
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { scope?: 'independent' | 'keyed' };
  }
}

/** Reset every query that depends on the scope without carrying it in its key. */
export function resetScopeDependentQueries(queryClient: QueryClient): Promise<void> {
  return queryClient.resetQueries({ predicate: query => query.meta?.scope === undefined });
}

export const sessionKeys = {
  all: ['session'] as const,
  identity: ['session', 'identity'] as const,
  orgTree: (userId: string) => ['session', 'org-tree', userId] as const,
  access: (userId: string, orgNodeId: string | null) =>
    ['session', 'access', userId, orgNodeId] as const,
};

// `client` is the app's one session client: a transport, not an input to any answer.
/* eslint-disable @tanstack/query/exhaustive-deps */

/** The logged-in user; null when there is no session. */
export const identityQueryOptions = (client: SessionClient) =>
  queryOptions({
    queryKey: sessionKeys.identity,
    queryFn: () => fetchIdentity(client),
    meta: { scope: 'independent' },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

/** The nodes the user may scope to. */
export const orgTreeQueryOptions = (client: SessionClient, userId: string) =>
  queryOptions({
    queryKey: sessionKeys.orgTree(userId),
    queryFn: () => fetchOrgNodeTree(client),
    meta: { scope: 'independent' },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

/** Effective permissions and entitlements at a node. */
export const accessQueryOptions = (
  client: SessionClient,
  userId: string,
  orgNodeId: string | null,
) =>
  queryOptions({
    queryKey: sessionKeys.access(userId, orgNodeId),
    queryFn: () => fetchAccess(client, orgNodeId),
    meta: { scope: 'keyed' },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

/* eslint-enable @tanstack/query/exhaustive-deps */
